import { Layout } from 'antd';
import AlertNavigation from './AlertNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import { functions } from '../../firebase';
import MonitoringDeviceRulesPageContent from './MonitoringDeviceRulesPageContent';
import { normalizeMonitoringDeviceRulesResponse } from './helpers';
import { MonitoringDeviceRuleOverrideInput, MonitoringDeviceRulesResponse } from './types';

const { Content } = Layout;

async function fetchMonitoringDeviceRules(
  sensorId: string
): Promise<MonitoringDeviceRulesResponse> {
  const settings = functions.httpsCallable('settings');
  const response: any = await settings({
    requestType: 'GET_MONITORING_DEVICE_RULES',
    sensorId,
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode && errorCode !== 200) {
    throw new Error(`Settings API error: ${errorCode}`);
  }

  return normalizeMonitoringDeviceRulesResponse(response.data);
}

async function saveMonitoringDeviceRules(input: MonitoringDeviceRuleOverrideInput) {
  const settings = functions.httpsCallable('settings');
  const response: any = await settings({
    requestType: 'UPSERT_MONITORING_DEVICE_RULE_OVERRIDE',
    ...input,
  });

  const errorCode = response?.data?.httpErrorCode?.status;
  if (errorCode && errorCode !== 200) {
    throw new Error(`Settings API error: ${errorCode}`);
  }

  return normalizeMonitoringDeviceRulesResponse(response.data);
}

function MonitoringDeviceRulesContainer() {
  const { sensorId } = useParams<{ sensorId: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['monitoring-device-rules', sensorId],
    queryFn: () => fetchMonitoringDeviceRules(sensorId),
    enabled: Boolean(sensorId),
  });

  const mutation = useMutation({
    mutationFn: saveMonitoringDeviceRules,
    onSuccess: async () => {
      toast.success('Enhedsregler gemt');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['monitoring-device-rules', sensorId] }),
        queryClient.invalidateQueries({ queryKey: ['monitoring-rules'] }),
        queryClient.invalidateQueries({ queryKey: ['monitoring-overview'] }),
      ]);
    },
    onError: (mutationError: unknown) => {
      toast.error(`Kunne ikke gemme enhedsregler: ${(mutationError as Error)?.message}`);
    },
  });

  return (
    <Layout>
      <HeaderBar />
      <Content
        style={{
          margin: '24px 24px 0',
          padding: '0 24px',
        }}
      >
        <AlertNavigation active="legacy" />
        <p className="mb-4 text-sm text-muted-foreground">
          Dette er enhedens ældre regelopsætning. Kontrollér alarmstyringen under Enheder og
          dækning, før du ændrer reglerne.
        </p>
        {isLoading && (
          <div className="py-12 text-center text-sm text-muted-foreground">
            Indlæser enhedsregler...
          </div>
        )}
        {isError && (
          <div className="py-12 text-center text-sm text-red-600">
            Kunne ikke hente enhedsregler: {(error as Error)?.message}
          </div>
        )}
        {data && (
          <MonitoringDeviceRulesPageContent
            response={data}
            isSaving={mutation.isPending}
            onSave={mutation.mutateAsync}
          />
        )}
      </Content>
      <FooterBar />
    </Layout>
  );
}

export default MonitoringDeviceRulesContainer;
